import { useState } from "react";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const todayISO = () => new Date().toISOString().split("T")[0];
const daysTo   = (d) => Math.floor((new Date(d) - Date.now()) / 86400000);
const daysAgo  = (d) => Math.floor((Date.now() - new Date(d)) / 86400000);
const addDays  = (d, n) => { const dt = new Date(d); dt.setDate(dt.getDate() + n); return dt.toISOString().split("T")[0]; };

const ITEM_ICONS_DEFAULT = {
  "Solution": "🧴", "Lens case": "🥽", "Lenses": "👓",
  "Enzyme cleaner": "🧪", "Rewetting drops": "💧", "Other": "📦",
};
const REM_ICONS_DEFAULT = {
  "Lens replacement":"👓","Solution restock":"🧴","Case replacement":"🥽",
  "Appointment":"📅","Inspection":"🔬","Custom":"🔔",
};
function getItemIcon(key, customIcons) {
  return (customIcons&&customIcons[key])||ITEM_ICONS_DEFAULT[key]||REM_ICONS_DEFAULT[key]||"📦";
}

const STATUS_COLOR = (pct) =>
  pct <= 0 ? "#E53935" : pct <= 25 ? "#FB8C00" : pct <= 50 ? "#FFC107" : "#4CAF50";

// ─── Stock bar ────────────────────────────────────────────────────────────────

function StockBar({ t, pct, label }) {
  const color = STATUS_COLOR(pct);
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
        <span style={{ fontSize: 11, color: t.muted }}>{label}</span>
        <span style={{ fontSize: 11, fontWeight: 700, color }}>{Math.round(pct)}%</span>
      </div>
      <div style={{ height: 6, borderRadius: 3, background: t.soft, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${Math.max(0, Math.min(100, pct))}%`, background: color, borderRadius: 3, transition: "width 0.4s ease" }} />
      </div>
    </div>
  );
}

// ─── Mark Done modal ──────────────────────────────────────────────────────────

function MarkDoneModal({ t, reminder, stockItems, onDone, onClose, customIcons={} }) {
  const [choice, setChoice] = useState(null); // "stock" | "manual"
  const [selectedStock, setSelectedStock] = useState(null);
  const [qty, setQty] = useState(1);

  // Match reminder type to stock items
  const matchingStock = stockItems.filter(s => {
    if (reminder.type === "Lens replacement") return s.item === "Lenses";
    if (reminder.type === "Solution restock")  return s.item === "Solution";
    if (reminder.type === "Case replacement")  return s.item === "Lens case";
    return true;
  });

  const handleConfirm = () => {
    onDone({
      reminder,
      useStock: choice === "stock",
      stockItem: choice === "stock" ? selectedStock : null,
      qtyUsed: choice === "stock" ? qty : 0,
    });
    onClose();
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 300, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: t.card, borderRadius: "24px 24px 0 0", padding: "24px 20px 44px", width: "100%", maxWidth: 430 }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <div style={{ fontWeight: 700, fontSize: 16, color: t.text }}>✅ Mark as done</div>
          <button onClick={onClose} style={{ background: t.soft, border: "none", borderRadius: 10, width: 32, height: 32, cursor: "pointer", color: t.muted, fontSize: 16 }}>✕</button>
        </div>
        <div style={{ fontSize: 13, color: t.muted, marginBottom: 20 }}>{reminder.title || reminder.type}</div>

        {/* Choice buttons */}
        <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
          {[
            { id: "stock",  icon: "📦", label: "Use from stock",  sub: "Reduce inventory" },
            { id: "manual", icon: "✔️", label: "Just mark done",   sub: "No stock change" },
          ].map(c => (
            <button key={c.id} onClick={() => { setChoice(c.id); if (c.id === "manual") setSelectedStock(null); }}
              style={{ flex: 1, padding: "14px 10px", borderRadius: 16, border: `2px solid ${choice === c.id ? t.primary : t.secondary + "60"}`, background: choice === c.id ? t.soft : t.card, cursor: "pointer", textAlign: "center", transition: "all 0.15s" }}>
              <div style={{ fontSize: 24, marginBottom: 4 }}>{c.icon}</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: choice === c.id ? t.primary : t.text }}>{c.label}</div>
              <div style={{ fontSize: 10, color: t.muted, marginTop: 2 }}>{c.sub}</div>
            </button>
          ))}
        </div>

        {/* Stock picker */}
        {choice === "stock" && (
          <div style={{ marginBottom: 20, animation: "fadeIn 0.2s ease" }}>
            <div style={{ fontSize: 12, color: t.muted, marginBottom: 8 }}>Which stock item to use?</div>
            {matchingStock.length === 0 ? (
              <div style={{ fontSize: 13, color: t.muted, background: t.soft, borderRadius: 12, padding: "12px", textAlign: "center" }}>
                No matching stock found. Add items to your inventory first.
              </div>
            ) : (
              <>
                {matchingStock.map(s => {
                  const pct = s.minQty ? Math.min(100, ((s.currentQty || 0) / s.minQty) * 100) : 100;
                  return (
                    <button key={s.id} onClick={() => setSelectedStock(s)}
                      style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "12px", borderRadius: 14, border: `2px solid ${selectedStock?.id === s.id ? t.primary : t.secondary + "40"}`, background: selectedStock?.id === s.id ? t.soft : t.card, cursor: "pointer", marginBottom: 8, textAlign: "left" }}>
                      <span style={{ fontSize: 24 }}>{getItemIcon(s.item, customIcons) || "📋"}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, color: t.text, fontSize: 13 }}>{s.item}{s.brand ? ` — ${s.brand}` : ""}</div>
                        <div style={{ fontSize: 11, color: t.muted }}>Stock: {s.currentQty || 0} {s.unit || ""}</div>
                        <StockBar t={t} pct={pct} label="" />
                      </div>
                    </button>
                  );
                })}

                {selectedStock && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 12, color: t.muted, marginBottom: 6 }}>Quantity to deduct</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <button onClick={() => setQty(q => Math.max(1, q - 1))}
                        style={{ width: 36, height: 36, borderRadius: 10, border: `1px solid ${t.secondary}`, background: t.soft, cursor: "pointer", fontSize: 18, color: t.text }}>−</button>
                      <span style={{ fontSize: 18, fontWeight: 700, color: t.text, minWidth: 32, textAlign: "center" }}>{qty}</span>
                      <button onClick={() => setQty(q => Math.min(q + 1, selectedStock.currentQty || 99))}
                        style={{ width: 36, height: 36, borderRadius: 10, border: `1px solid ${t.secondary}`, background: t.soft, cursor: "pointer", fontSize: 18, color: t.text }}>+</button>
                      <span style={{ fontSize: 12, color: t.muted }}>{selectedStock.unit || ""}</span>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Confirm button */}
        {choice && (
          <button onClick={handleConfirm}
            disabled={choice === "stock" && !selectedStock}
            style={{ width: "100%", padding: 14, borderRadius: 14, border: "none", background: (choice === "stock" && !selectedStock) ? "#ddd" : t.grad, color: (choice === "stock" && !selectedStock) ? "#999" : "#fff", fontWeight: 700, fontSize: 15, cursor: (choice === "stock" && !selectedStock) ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
            {choice === "stock" ? `Confirm — deduct ${qty} ${selectedStock?.unit || "unit(s)"}` : "Mark as done"}
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Stock item card ──────────────────────────────────────────────────────────

function StockCard({ t, item, onEdit, onDelete, onTopUp, customIcons={} }) {
  const curr = parseFloat(item.currentQty) || 0;
  const min  = parseFloat(item.minQty) || 0;
  const max  = parseFloat(item.maxQty) || min * 2 || 10;
  const pct  = min > 0 ? Math.min(100, (curr / min) * 100) : (curr > 0 ? 100 : 0);
  const color = STATUS_COLOR(pct);
  const needsTopUp = min > 0 && curr <= min;

  return (
    <div style={{ background: t.card, borderRadius: 18, padding: "16px", marginBottom: 10, boxShadow: "0 2px 10px rgba(0,0,0,0.05)", border: `1px solid ${needsTopUp ? color + "40" : t.secondary + "25"}`, position: "relative", overflow: "hidden" }}>
      {needsTopUp && (
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: color }} />
      )}

      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        {/* Icon + status */}
        <div style={{ width: 52, height: 52, borderRadius: 14, background: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, flexShrink: 0 }}>
          {getItemIcon(item.item, customIcons) || "📋"}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <div style={{ fontWeight: 700, color: t.text, fontSize: 14 }}>{item.item}</div>
              {item.brand && <div style={{ fontSize: 12, color: t.muted }}>{item.brand}</div>}
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <button onClick={onEdit} style={{ background: t.soft, border: "none", borderRadius: 8, padding: "5px 8px", cursor: "pointer", fontSize: 13 }}>✏️</button>
              <button onClick={onDelete} style={{ background: "#fff0f0", border: "none", borderRadius: 8, padding: "5px 8px", cursor: "pointer", fontSize: 13 }}>🗑</button>
            </div>
          </div>

          {/* Current stock display */}
          <div style={{ display: "flex", alignItems: "baseline", gap: 6, margin: "8px 0 6px" }}>
            <span style={{ fontSize: 28, fontWeight: 800, color, lineHeight: 1 }}>{curr}</span>
            {min > 0 && <span style={{ fontSize: 13, color: t.muted }}>/ {min} min {item.unit || ""}</span>}
            {!min && <span style={{ fontSize: 13, color: t.muted }}>{item.unit || "units"}</span>}
          </div>

          {min > 0 && <StockBar t={t} pct={pct} label={needsTopUp ? "⚠️ Below minimum — top up needed" : "Stock level"} />}
        </div>
      </div>

      {/* Plan / notes */}
      {item.topUpPlan && (
        <div style={{ marginTop: 10, padding: "8px 12px", background: t.soft, borderRadius: 10, fontSize: 12, color: t.muted }}>
          📅 Top-up plan: {item.topUpPlan}
        </div>
      )}

      {/* Top-up button */}
      {needsTopUp && (
        <button onClick={onTopUp}
          style={{ width: "100%", marginTop: 10, padding: "10px", borderRadius: 12, border: "none", background: t.grad, color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
          + Log restock
        </button>
      )}
    </div>
  );
}

// ─── Stock edit modal ─────────────────────────────────────────────────────────

function StockModal({ t, initial, onSave, onClose, customIcons={} }) {
  const [d, setD] = useState(initial || {
    item: "Solution", brand: "", currentQty: "", unit: "bottle",
    minQty: "", maxQty: "", topUpPlan: "", notes: "",
  });
  const u = (k, v) => setD(p => ({ ...p, [k]: v }));

  const ITEMS = ["Solution", "Lens case", "Lenses", "Enzyme cleaner", "Rewetting drops", "Other"];
  const UNITS = ["bottle", "pair", "box", "pack", "piece", "ml", "set"];

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 200, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: t.card, borderRadius: "24px 24px 0 0", padding: "20px 20px 44px", width: "100%", maxWidth: 430, maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
          <div style={{ fontWeight: 700, fontSize: 16, color: t.text }}>{initial?.id ? "Edit stock item" : "Add stock item"}</div>
          <button onClick={onClose} style={{ background: t.soft, border: "none", borderRadius: 10, width: 32, height: 32, cursor: "pointer", fontSize: 16, color: t.muted }}>✕</button>
        </div>

        {/* Item type */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, color: "#888", marginBottom: 8 }}>Item type</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {ITEMS.map(item => (
              <button key={item} onClick={() => u("item", item)}
                style={{ padding: "7px 14px", borderRadius: 20, border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: 600,
                  background: d.item === item ? t.grad : t.soft, color: d.item === item ? "#fff" : t.muted }}>
                {getItemIcon(item, customIcons)} {item}
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>Brand (optional)</div>
          <input value={d.brand || ""} onChange={e => u("brand", e.target.value)} placeholder="e.g. Menicon Progent"
            style={{ width: "100%", padding: "10px 12px", border: "1px solid #ddd", borderRadius: 10, fontSize: 14, fontFamily: "inherit", boxSizing: "border-box" }} />
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>Unit</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {UNITS.map(u2 => (
              <button key={u2} onClick={() => u("unit", u2)}
                style={{ padding: "5px 12px", borderRadius: 16, border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 11, fontWeight: 600,
                  background: d.unit === u2 ? t.primary : t.soft, color: d.unit === u2 ? "#fff" : t.muted }}>
                {u2}
              </button>
            ))}
          </div>
        </div>

        {/* Stock quantities */}
        <div style={{ background: t.soft, borderRadius: 14, padding: "14px", marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: t.text, marginBottom: 12 }}>Stock levels</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            {[
              { label: "Current stock", key: "currentQty", hint: "How many you have now" },
              { label: "Minimum (alert)", key: "minQty", hint: "Alert below this" },
              { label: "Maximum (target)", key: "maxQty", hint: "Top-up target" },
            ].map(f => (
              <div key={f.key}>
                <div style={{ fontSize: 10, color: t.muted, marginBottom: 4 }}>{f.label}</div>
                <input type="number" value={d[f.key] || ""} onChange={e => u(f.key, e.target.value)} placeholder="0"
                  style={{ width: "100%", padding: "9px 8px", border: "1px solid #ddd", borderRadius: 10, fontSize: 16, fontWeight: 700, fontFamily: "inherit", boxSizing: "border-box", textAlign: "center" }} />
                <div style={{ fontSize: 9, color: t.muted, marginTop: 3, textAlign: "center" }}>{f.hint}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Top-up plan */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>Top-up plan (optional)</div>
          <input value={d.topUpPlan || ""} onChange={e => u("topUpPlan", e.target.value)}
            placeholder="e.g. Buy 2 bottles every month from Guardian"
            style={{ width: "100%", padding: "10px 12px", border: "1px solid #ddd", borderRadius: 10, fontSize: 13, fontFamily: "inherit", boxSizing: "border-box" }} />
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>Notes</div>
          <textarea value={d.notes || ""} onChange={e => u("notes", e.target.value)} rows={2}
            placeholder="e.g. Available at Caring Pharmacy, RM 45/bottle"
            style={{ width: "100%", padding: "10px 12px", border: "1px solid #ddd", borderRadius: 10, fontSize: 13, fontFamily: "inherit", resize: "vertical", boxSizing: "border-box" }} />
        </div>

        <button onClick={() => { onSave(d); onClose(); }}
          style={{ width: "100%", padding: 14, borderRadius: 14, border: "none", background: t.grad, color: "#fff", fontWeight: 700, fontSize: 15, cursor: "pointer", fontFamily: "inherit" }}>
          Save item
        </button>
      </div>
    </div>
  );
}

// ─── Reminder item ────────────────────────────────────────────────────────────

function ReminderCard({ t, rem, onEdit, onDelete, onMarkDone }) {
  const d = rem.dueDate ? daysTo(rem.dueDate) : null;
  const overdue = d !== null && d < 0;
  const dueToday = d === 0;
  const urgent = d !== null && d <= 3 && d >= 0;

  const color = overdue ? "#E53935" : dueToday ? "#E53935" : urgent ? "#FB8C00" : t.primary;
  const pct = rem.dueDate && rem.repeatDays
    ? Math.max(0, Math.min(100, (daysAgo(rem.dueDate) / +rem.repeatDays) * 100))
    : 50;

  const statusLabel = overdue
    ? `Overdue by ${Math.abs(d)} day${Math.abs(d) > 1 ? "s" : ""}`
    : dueToday ? "Due today!"
    : d !== null ? `${d} day${d !== 1 ? "s" : ""} remaining`
    : "";

  return (
    <div style={{ background: t.card, borderRadius: 18, padding: "14px", marginBottom: 10, boxShadow: "0 2px 10px rgba(0,0,0,0.05)", border: `1.5px solid ${overdue || dueToday ? color + "50" : t.secondary + "25"}`, position: "relative", overflow: "hidden" }}>
      {(overdue || dueToday) && <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: color }} />}

      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        {/* Progress ring */}
        <div style={{ position: "relative", flexShrink: 0 }}>
          <svg width={52} height={52} style={{ transform: "rotate(-90deg)" }}>
            <circle cx={26} cy={26} r={22} fill="none" stroke={t.soft} strokeWidth={5}/>
            <circle cx={26} cy={26} r={22} fill="none" stroke={color} strokeWidth={5}
              strokeDasharray={`${2*Math.PI*22 * Math.min(pct/100,1)} ${2*Math.PI*22}`} strokeLinecap="round"/>
          </svg>
          <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", fontSize: 18 }}>
            {rem.type === "Lens replacement" ? "👁️" : rem.type === "Solution restock" ? "💧" : rem.type === "Case replacement" ? "📦" : rem.type === "Inspection" ? "🔬" : "🔔"}
          </div>
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, color: t.text, fontSize: 14 }}>{rem.title || rem.type}</div>
          <div style={{ fontSize: 12, fontWeight: overdue || dueToday ? 700 : 400, color }}>{statusLabel}</div>
          {rem.repeatDays && <div style={{ fontSize: 11, color: t.muted }}>Repeats every {rem.repeatDays} days</div>}
        </div>

        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
          <button onClick={onEdit} style={{ background: t.soft, border: "none", borderRadius: 9, padding: "6px 8px", cursor: "pointer", fontSize: 13 }}>✏️</button>
          <button onClick={onDelete} style={{ background: "#fff0f0", border: "none", borderRadius: 9, padding: "6px 8px", cursor: "pointer", fontSize: 13 }}>🗑</button>
        </div>
      </div>

      {/* Mark done button — shown when overdue or due soon */}
      {(overdue || dueToday || urgent) && (
        <button onClick={onMarkDone}
          style={{ width: "100%", marginTop: 10, padding: "10px", borderRadius: 12, border: "none", background: overdue || dueToday ? t.grad : t.soft, color: overdue || dueToday ? "#fff" : t.accent, fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
          ✅ Mark as done
        </button>
      )}
    </div>
  );
}

// ─── Main inventory screen ────────────────────────────────────────────────────

export default function InventoryScreen({
  t, remList, stockList, onSaveStock, onSaveRem, onDeleteStock, onDeleteRem, onMarkDone, onLogRestock, customIcons = {},
}) {
  const [subTab, setSubTab] = useState("reminders"); // "reminders" | "stock" | "plan"
  const [showStockModal, setShowStockModal] = useState(false);
  const [showRemModal, setShowRemModal] = useState(false);
  const [editStock, setEditStock] = useState(null);
  const [editRem, setEditRem] = useState(null);
  const [markDoneRem, setMarkDoneRem] = useState(null);

  const overdueRems  = remList.filter(r => r.dueDate && daysTo(r.dueDate) < 0);
  const dueTodayRems = remList.filter(r => r.dueDate && daysTo(r.dueDate) === 0);
  const upcomingRems = remList.filter(r => r.dueDate && daysTo(r.dueDate) > 0);
  const lowStock     = stockList.filter(s => s.minQty && parseFloat(s.currentQty || 0) <= parseFloat(s.minQty));

  return (
    <div style={{ fontFamily: "inherit" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 18, color: t.text }}>Reminders & Stock</div>
          <div style={{ fontSize: 12, color: t.muted, marginTop: 2 }}>
            {overdueRems.length > 0 && <span style={{ color: "#E53935", fontWeight: 600 }}>{overdueRems.length} overdue · </span>}
            {dueTodayRems.length > 0 && <span style={{ color: "#FB8C00", fontWeight: 600 }}>{dueTodayRems.length} due today · </span>}
            {lowStock.length > 0 && <span style={{ color: "#FB8C00", fontWeight: 600 }}>{lowStock.length} item{lowStock.length > 1 ? "s" : ""} low</span>}
            {overdueRems.length === 0 && dueTodayRems.length === 0 && lowStock.length === 0 && "All good ✓"}
          </div>
        </div>
        <button onClick={() => { setEditStock(null); setEditRem(null); subTab === "stock" ? setShowStockModal(true) : setShowRemModal(true); }}
          style={{ background: t.grad, border: "none", borderRadius: 14, padding: "8px 16px", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
          + Add
        </button>
      </div>

      {/* Sub-tabs */}
      <div style={{ display: "flex", background: t.soft, borderRadius: 14, padding: 4, marginBottom: 16, gap: 3 }}>
        {[
          { id: "reminders", label: "🔔 Reminders", badge: overdueRems.length + dueTodayRems.length },
          { id: "stock",     label: "📦 Inventory",  badge: lowStock.length },
          { id: "plan",      label: "📅 Top-up plan", badge: 0 },
        ].map(st => (
          <button key={st.id} onClick={() => setSubTab(st.id)}
            style={{ flex: 1, padding: "9px 6px", border: "none", borderRadius: 11, cursor: "pointer", fontFamily: "inherit", fontWeight: 700, fontSize: 11, position: "relative", transition: "all 0.2s",
              background: subTab === st.id ? t.card : "transparent",
              color: subTab === st.id ? t.primary : t.muted,
              boxShadow: subTab === st.id ? "0 2px 8px rgba(0,0,0,0.08)" : "none" }}>
            {st.label}
            {st.badge > 0 && (
              <span style={{ position: "absolute", top: 4, right: 4, background: "#E53935", color: "#fff", fontSize: 9, fontWeight: 700, borderRadius: 10, padding: "1px 5px", minWidth: 14, textAlign: "center" }}>
                {st.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── REMINDERS tab ── */}
      {subTab === "reminders" && (
        <>
          {remList.length === 0 && (
            <div style={{ background: t.card, borderRadius: 18, padding: 32, textAlign: "center", boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}>
              <div style={{ fontSize: 44, marginBottom: 12 }}>🔔</div>
              <div style={{ fontWeight: 700, color: t.text, marginBottom: 6 }}>No reminders</div>
              <div style={{ fontSize: 13, color: t.muted, marginBottom: 18 }}>Add a lens profile to auto-create reminders, or add manually.</div>
              <button onClick={() => setShowRemModal(true)}
                style={{ background: t.grad, border: "none", borderRadius: 14, padding: "11px 24px", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
                Add reminder
              </button>
            </div>
          )}

          {overdueRems.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#E53935", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>Overdue</div>
              {overdueRems.map(rem => (
                <ReminderCard key={rem.id} t={t} rem={rem}
                  onEdit={() => { setEditRem(rem); setShowRemModal(true); }}
                  onDelete={() => onDeleteRem(rem.id)}
                  onMarkDone={() => setMarkDoneRem(rem)} />
              ))}
            </div>
          )}

          {dueTodayRems.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#FB8C00", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>Due today</div>
              {dueTodayRems.map(rem => (
                <ReminderCard key={rem.id} t={t} rem={rem}
                  onEdit={() => { setEditRem(rem); setShowRemModal(true); }}
                  onDelete={() => onDeleteRem(rem.id)}
                  onMarkDone={() => setMarkDoneRem(rem)} />
              ))}
            </div>
          )}

          {upcomingRems.length > 0 && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: t.muted, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>Upcoming</div>
              {upcomingRems.map(rem => (
                <ReminderCard key={rem.id} t={t} rem={rem}
                  onEdit={() => { setEditRem(rem); setShowRemModal(true); }}
                  onDelete={() => onDeleteRem(rem.id)}
                  onMarkDone={() => setMarkDoneRem(rem)} />
              ))}
            </div>
          )}
        </>
      )}

      {/* ── STOCK tab ── */}
      {subTab === "stock" && (
        <>
          {stockList.length === 0 && (
            <div style={{ background: t.card, borderRadius: 18, padding: 32, textAlign: "center", boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}>
              <div style={{ fontSize: 44, marginBottom: 12 }}>📦</div>
              <div style={{ fontWeight: 700, color: t.text, marginBottom: 6 }}>No stock items</div>
              <div style={{ fontSize: 13, color: t.muted, marginBottom: 18 }}>Track your solution, cases, and lenses inventory.</div>
              <button onClick={() => { setEditStock(null); setShowStockModal(true); }}
                style={{ background: t.grad, border: "none", borderRadius: 14, padding: "11px 24px", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
                Add stock item
              </button>
            </div>
          )}

          {lowStock.length > 0 && (
            <div style={{ background: "#FFF8E1", border: "1px solid #FFE082", borderRadius: 14, padding: "12px 14px", marginBottom: 14 }}>
              <div style={{ fontWeight: 700, color: "#F57F17", fontSize: 13, marginBottom: 4 }}>⚠️ Low stock alert</div>
              <div style={{ fontSize: 12, color: "#795548" }}>
                {lowStock.map(s => `${s.item}${s.brand ? " ("+s.brand+")" : ""}`).join(", ")} {lowStock.length === 1 ? "is" : "are"} running low. Time to top up!
              </div>
            </div>
          )}

          {stockList.map(item => (
            <StockCard key={item.id} t={t} item={item} customIcons={customIcons}
              onEdit={() => { setEditStock(item); setShowStockModal(true); }}
              onDelete={() => onDeleteStock(item.id)}
              onTopUp={() => onLogRestock(item)} />
          ))}
        </>
      )}

      {/* ── PLAN tab ── */}
      {subTab === "plan" && (
        <>
          <div style={{ background: t.card, borderRadius: 18, padding: "16px", marginBottom: 12, boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: t.text, marginBottom: 14 }}>📅 Top-up schedule</div>
            {stockList.length === 0 ? (
              <div style={{ fontSize: 13, color: t.muted, textAlign: "center", padding: "16px 0" }}>Add stock items first to see your top-up plan.</div>
            ) : (
              stockList.map((s, i) => {
                const curr = parseFloat(s.currentQty) || 0;
                const min  = parseFloat(s.minQty) || 0;
                const max  = parseFloat(s.maxQty) || min * 2 || 10;
                const needed = Math.max(0, max - curr);
                const pct = min > 0 ? Math.min(100, (curr / min) * 100) : 100;
                return (
                  <div key={s.id} style={{ padding: "12px 0", borderBottom: i < stockList.length - 1 ? `1px solid ${t.soft}` : "none" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 20 }}>{getItemIcon(s.item, customIcons) || "📋"}</span>
                        <div>
                          <div style={{ fontWeight: 600, color: t.text, fontSize: 13 }}>{s.item}{s.brand ? ` — ${s.brand}` : ""}</div>
                          <div style={{ fontSize: 11, color: t.muted }}>{curr} / {min} {s.unit || ""} minimum</div>
                        </div>
                      </div>
                      {needed > 0 && (
                        <div style={{ background: `${STATUS_COLOR(pct)}18`, color: STATUS_COLOR(pct), fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 12 }}>
                          Need {needed} {s.unit || ""}
                        </div>
                      )}
                    </div>
                    <StockBar t={t} pct={pct} label="" />
                    {s.topUpPlan && (
                      <div style={{ marginTop: 8, fontSize: 12, color: t.muted, display: "flex", gap: 6, alignItems: "flex-start" }}>
                        <span>📋</span><span>{s.topUpPlan}</span>
                      </div>
                    )}
                    {!s.topUpPlan && (
                      <button onClick={() => { setEditStock(s); setShowStockModal(true); }}
                        style={{ marginTop: 8, fontSize: 11, color: t.primary, background: "none", border: `1px dashed ${t.secondary}`, borderRadius: 8, padding: "4px 10px", cursor: "pointer", fontFamily: "inherit" }}>
                        + Add top-up plan
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Summary */}
          {stockList.length > 0 && (
            <div style={{ background: t.soft, borderRadius: 14, padding: "14px", fontSize: 12, color: t.muted, lineHeight: 1.8 }}>
              💡 <strong style={{ color: t.text }}>Tip:</strong> Set a top-up plan for each item (e.g. "Buy 2 bottles monthly from Guardian") and a minimum stock level. When stock drops below minimum, you'll see an alert and the reminder will prompt you to restock.
            </div>
          )}
        </>
      )}

      {/* Modals */}
      {showStockModal && (
        <StockModal t={t} initial={editStock} onSave={onSaveStock} onClose={() => { setShowStockModal(false); setEditStock(null); }} customIcons={customIcons}/>
      )}

      {showRemModal && (
        <RemModal t={t} initial={editRem} onSave={onSaveRem} onClose={() => { setShowRemModal(false); setEditRem(null); }} />
      )}

      {markDoneRem && (
        <MarkDoneModal t={t} reminder={markDoneRem} stockItems={stockList} onDone={onMarkDone} onClose={() => setMarkDoneRem(null)} />
      )}
    </div>
  );
}

// ─── Simple reminder modal (inline to avoid extra import) ─────────────────────

function RemModal({ t, initial, onSave, onClose }) {
  const TYPES = ["Lens replacement","Solution restock","Case replacement","Appointment","Inspection","Custom"];
  const [d, setD] = useState(initial || { title:"", type:"Lens replacement", dueDate:todayISO(), repeatDays:"30", notes:"" });
  const u = (k, v) => setD(p => ({ ...p, [k]: v }));
  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.45)", zIndex:200, display:"flex", alignItems:"flex-end", justifyContent:"center" }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:t.card, borderRadius:"24px 24px 0 0", padding:"20px 20px 44px", width:"100%", maxWidth:430, maxHeight:"88vh", overflowY:"auto" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18 }}>
          <div style={{ fontWeight:700, fontSize:16, color:t.text }}>{initial?.id?"Edit reminder":"Add reminder"}</div>
          <button onClick={onClose} style={{ background:t.soft, border:"none", borderRadius:10, width:32, height:32, cursor:"pointer", fontSize:16, color:t.muted }}>✕</button>
        </div>
        <div style={{ marginBottom:12 }}>
          <div style={{ fontSize:12, color:"#888", marginBottom:4 }}>Title</div>
          <input value={d.title||""} onChange={e=>u("title",e.target.value)} placeholder="e.g. Replace Ortho-K lenses"
            style={{ width:"100%", padding:"10px 12px", border:"1px solid #ddd", borderRadius:10, fontSize:14, fontFamily:"inherit", boxSizing:"border-box" }}/>
        </div>
        <div style={{ marginBottom:12 }}>
          <div style={{ fontSize:12, color:"#888", marginBottom:8 }}>Type</div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
            {TYPES.map(tp=>(
              <button key={tp} onClick={()=>u("type",tp)}
                style={{ padding:"6px 12px", borderRadius:18, border:"none", cursor:"pointer", fontFamily:"inherit", fontSize:11, fontWeight:600, background:d.type===tp?t.grad:t.soft, color:d.type===tp?"#fff":t.muted }}>
                {tp}
              </button>
            ))}
          </div>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:12 }}>
          <div>
            <div style={{ fontSize:12, color:"#888", marginBottom:4 }}>Due date</div>
            <input type="date" value={d.dueDate||""} onChange={e=>u("dueDate",e.target.value)}
              style={{ width:"100%", padding:"10px 12px", border:"1px solid #ddd", borderRadius:10, fontSize:14, fontFamily:"inherit", boxSizing:"border-box" }}/>
          </div>
          <div>
            <div style={{ fontSize:12, color:"#888", marginBottom:4 }}>Repeat (days)</div>
            <input type="number" value={d.repeatDays||""} onChange={e=>u("repeatDays",e.target.value)} placeholder="30"
              style={{ width:"100%", padding:"10px 12px", border:"1px solid #ddd", borderRadius:10, fontSize:14, fontFamily:"inherit", boxSizing:"border-box" }}/>
          </div>
        </div>
        <div style={{ marginBottom:20 }}>
          <div style={{ fontSize:12, color:"#888", marginBottom:4 }}>Notes</div>
          <textarea value={d.notes||""} onChange={e=>u("notes",e.target.value)} rows={2}
            style={{ width:"100%", padding:"10px 12px", border:"1px solid #ddd", borderRadius:10, fontSize:13, fontFamily:"inherit", resize:"vertical", boxSizing:"border-box" }}/>
        </div>
        <button onClick={()=>{ onSave(d); onClose(); }}
          style={{ width:"100%", padding:14, borderRadius:14, border:"none", background:t.grad, color:"#fff", fontWeight:700, fontSize:15, cursor:"pointer", fontFamily:"inherit" }}>
          Save reminder
        </button>
      </div>
    </div>
  );
}
